
import { combineLatest as observableCombineLatest } from 'rxjs';
import { Component, ViewChild, ViewContainerRef, inject } from '@angular/core';
import { UserService } from '@tytapp/user';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { v4 as uuid } from 'uuid';
import {
  Transfer, Page, PageComponent, AppConfig, Redirection, OpenGraph, Cookies
} from '@tytapp/common';
import { ReCaptchaV3Service } from 'ng-recaptcha';


import { ApiOfferSet, ApiOffer, OfferSetsApi, ApiConfiguration, ApiUser } from '@tytapp/api';
import { OFFER_SET_COMPONENTS, OfferSetTemplateBase } from '../offers-templates';
import { environment } from 'environments/environment';
import { isClientSide } from '../../environment-utils';

@Page('offers')
@Component({
  selector: 'tyt-offers',
  templateUrl: './offers.component.html',
  styleUrls: ['./offers.component.scss']
})
export class OffersComponent extends PageComponent {
  private userService = inject(UserService);
  private route = inject(ActivatedRoute);
  private appConfig = inject(AppConfig);
  private redirection = inject(Redirection);
  private opengraph = inject(OpenGraph);
  private offerSetApi = inject(OfferSetsApi);
  private cookies = inject(Cookies);
  private apiConfig = inject(ApiConfiguration);
  private recaptchaV3Service = inject(ReCaptchaV3Service);

  get pageComponentId() {
    return 'offers';
  }


  @Transfer()
  user: ApiUser;

  @Transfer()
  offersGroups = null;
  @Transfer()
  currentBackgroundImage: string;
  @Transfer()
  offerSet: ApiOfferSet;

  @Transfer()
  offer: ApiOffer;

  @Transfer()
  offersVersionToShow: string = 'classic';

  @Transfer()
  step: string;

  @Transfer()
  urlStyle = 'classic';

  @Transfer()
  impressionUuid: string = null;

  recaptchaToken: string = null;

  @ViewChild('offerSetTemplate', { read: ViewContainerRef, static: true }) offerSetComponentContainer: ViewContainerRef;

  async init() {
    this.setupSplitTestClientID();


    this.impressionUuid ??= uuid();

    if (isClientSide()) {
      this.recaptchaV3Service.execute('offer_set_impression').subscribe(
        token => {
          this.recaptchaToken = token;
        },
        error => {
          console.error('reCaptcha check failed:', error);
        }
      );
    }

    let observer = observableCombineLatest(
      this.route.paramMap,
      this.route.queryParamMap,
      this.route.data,
      (params, query, data) => ({ params, query, data })
    );

    observer.subscribe(({ params, query, data }) => {
      if (this.isFirstParamChange) {
        this.isFirstParamChange = false;
      } else {
        this.impressionUuid = null;
        this.recaptchaToken = null;
      }

      if (typeof window !== 'undefined' && query.get('preview')) {
        this.previewOfferSet = JSON.parse(atob(query.get('preview')));
      } else {
        this.previewOfferSet = null;
      }
      this.offerSetId = params.get('id');
      this.offerUUID = params.get('offerId');

      if (query.has('t')) {
        this.templateOverride = query.get('t');
      } else {
        this.templateOverride = undefined;
      }

      // rawOfferUUID stores the original offerUUID value before it's processed.
      this.rawOfferUUID = this.offerUUID;

      if (this.offerUUID && this.offerUUID.startsWith('p:')) {
        let [type, price, period] = this.offerUUID.split(/:/);
        this.price = Number(price);
        this.offerUUID = undefined; // offerUUID is cleared if it's a 'p:' type id.
      } else {
        this.price = undefined;
      }

      if (query.has('email'))
        this.knownEmail = query.get('email');

      if (query.has('firstName'))
        this.knownFirstName = query.get('firstName');

      this.showNoThanks = ['true', '1'].includes(query.get('showNoThanks'));

      this.step = params.get('step');
      this.urlStyle = data.urlStyle || 'classic';

      // The following query parameter is required in the Google Analytics goal tracking.
      // This prevents modern /join/XYZ URLs from appearing in Google Analytics goal tracking
      // for the Classic offer system goal sets.
      // If g != 1, the user will be redirected to g=1, and that URL is the entry URL for the
      // classic goals

      this.isClassicGoalStart = query.get('g');
      this.queryParams = query;

      // Fix up /join/m/:offerSetId URLs
      if (this.offerSetId === 'm') {
        this.redirection.go(`/join/${params.get('alternateOfferId')}`);
        return;
      }

      this.showOffers();
    });

    this.subscribe(this.userService.userChanged, user => this.user = user);

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        this.setBackgroundImage();
      });
    }
  }

  private offerSetId: string;
  private isFirstParamChange = true;
  private previewOfferSet: ApiOfferSet;
  get previewing() {
    return !!this.previewOfferSet;
  }

  knownEmail: string;
  knownFirstName: string;
  showNoThanks: boolean = false;

  offerUUID: string;
  templateOverride: string;
  price: number;
  rawOfferUUID: string;
  private isClassicGoalStart: string;
  private queryParams: ParamMap;

  get backgroundImage() {
    return this.currentBackgroundImage;
  }

  async setBackgroundImage() {
    if (!this.offerSet)
      return;

    if (typeof window === 'undefined') {
      this.currentBackgroundImage = this.offerSet.desktop_image;
      return;
    }

    if (window.innerWidth <= 580) {
      this.currentBackgroundImage = this.offerSet.mobile_image;
    } else if (window.innerWidth > 580 && window.innerWidth < 921) {
      this.currentBackgroundImage = this.offerSet.tablet_image;
    } else {
      this.currentBackgroundImage = this.offerSet.desktop_image;
    }
  }

  get pixelEndpoint() {
    let offerPart: string;

    // if rawOfferUUID exists and starts with 'p:', we use it and replace the colons with dots.
    if (this.rawOfferUUID && this.rawOfferUUID.startsWith('p:')) {
      const [type, price, period] = this.rawOfferUUID.split(':');
      offerPart = `${type}.${price}.${period}`; // format: p.amount.period
    } else {
      // otherwise, we use the processed offerUUID or 'x' if there isn't one.
      offerPart = this.offerUUID || 'x';
    }

    const offerSetPart = this.offerSet ? this.offerSet.uuid : this.offerSetId;
    const stepPart = this.step || 'pricing'; // default step is 'pricing'.

    return `${offerSetPart}:${offerPart}/${stepPart}`;
  }

  get pixelUrl() {
    let url = `${environment.urls.api}/membership/offer_sets/${this.pixelEndpoint}/impression`;
    const params: string[] = [];
    if (this.impressionUuid) {
      params.push(`uuid=${this.impressionUuid}`);
    }
    if (this.recaptchaToken) {
      params.push(`au=${this.recaptchaToken}`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    return url;
  }

  pixelImpression = false;

  private get hasOneTimeOffers() {
    if (!this.offerSet || !this.offerSet.offers)
      return false;

    return this.offerSet.offers.some(x => x.period === 'one_time');
  }

  private async showOffers() {
    this.pixelImpression = true; // both server and client
    if (!this.hasServerState) {
      if (!this.offerSet || this.offerSet.uuid !== this.offerSetId) {
        await this.fetchOfferSet(this.offerSetId);
      }

      if (this.offerSet && this.offerUUID) {
        this.offer = this.offerSet.offers.find(x => x.uuid === this.offerUUID);
      }
    }

    this.loadTemplate();
  }

  private async fetchOfferSet(offerSetId: string) {
    let offerSet: ApiOfferSet;

    if (this.previewOfferSet) {
      offerSet = this.previewOfferSet;
    } else {
      try {
        if (offerSetId) {
          // Note: Impression recording happens with a separate request later.
          offerSet = await this.offerSetApi.get(offerSetId, false).toPromise();
          if (offerSet && offerSet.uuid !== offerSetId) {
            // This is a rescinded offer set. Send them to /join
            //console.log(`Detected rescinded offer set. Navigating to /join`);
            this.redirection.go(`/join`);
            return;
          }
        } else {
          offerSet = await this.offerSetApi.default(false).toPromise();
          this.offerSetId = offerSet.uuid;
        }
      } catch (e) {
        console.error(`Caught error while trying to load offers:`);
        console.error(e);

        this.error = true;
        return;
      }
    }

    // Set page metadata
    this.opengraph.setMetadata({
      type: 'website',
      title: offerSet.embed_headline || `Support independent, progressive news coverage from TYT`,
      description: offerSet.embed_description || `Holding the establishment accountable is what we do. Staying committed to objective journalism is who we are.`,
      image: offerSet.embed_image_url || this.backgroundImage || `${environment.urls.accounts}/assets/offer-bg-2020.png`
    });

    // Set the style
    if (typeof offerSet.style !== 'undefined') {
      this.offersVersionToShow = offerSet.style;
    }
    // Some URL shenanigans

    if (offerSet.style === 'modern') {
      if (!this.step) {
        let forceUrlStep = await this.appConfig.featureEnabled('apps.accounts.force_url_step');
        if (forceUrlStep) {
          this.redirection.go(`/join/m/${offerSet.uuid}/x/pricing`);
          return;
        }
      }
    } else if (this.urlStyle === 'modern') {
      // This is a classic offer set but the URL is modern.
      // This should only happen if a classic offer set is default.
      // Fix with a redirect
      this.redirection.go(`/join/${offerSet.uuid}`);
      return;
    }

    // Bind the offerset to the view
    this.offerSet = offerSet;

    // Set the background according to the offer set
    this.setBackgroundImage();

    // Record an impression
    await this.recordImpression();
  }

  private async recordImpression() {
    let recordImpression = false;
    if (this.offerSet.style === 'modern') {
      recordImpression = (this.step || 'pricing') === 'pricing';
    } else if (this.offerSet.style === 'classic') {
      recordImpression = true;
    }

    if (recordImpression) {
      // this is an api call to record an impression, not the image pixel.
      // the image pixel is handled via this.pixelurl in the template.
      await this.offerSetApi.get(this.offerSet.uuid, true).toPromise();
    }
  }

  private loadTemplate() {
    let offerSet = this.offerSet;

    if (!offerSet)
      return;

    if (this.offerSet.style === 'modern') {
      this.setupTemplate(this.templateOverride || offerSet.template_name || 'default');
    } else {
      this.clearTemplate();
    }
  }

  private setupSplitTestClientID() {
    if (!this.apiConfig.globalHeaders['X-TYT-CID']) {
      let cid: string = this.cookies.get('tytcid');

      if (cid === null) {
        cid = uuid();
        this.cookies.set('tytcid', cid, 1000 * 60 * 60 * 24 * 365 * 10);
      }

      this.apiConfig.globalHeaders['X-TYT-CID'] = cid;
    }
  }

  template: OfferSetTemplateBase;

  private clearTemplate() {
    if (this.offerSetComponentContainer)
      this.offerSetComponentContainer.clear();
  }

  private _currentTemplateID: keyof typeof OFFER_SET_COMPONENTS;

  private setupTemplate(templateName: keyof typeof OFFER_SET_COMPONENTS): void {
    if (this._currentTemplateID === templateName) {
      return;
    }
    this._currentTemplateID = templateName;

    this.clearTemplate();

    let componentClass = OFFER_SET_COMPONENTS[templateName];

    if (!componentClass) {
      console.error(`OfferSet: No such template '${templateName}'`);
      componentClass = OFFER_SET_COMPONENTS[templateName = 'default'];
    }

    console.log(`OfferSet: Setting up layout named '${templateName}'`);

    const componentFactoryInstance = this.offerSetComponentContainer.createComponent(componentClass);
    this.template = componentFactoryInstance.instance;

    this.syncTemplateInputs();
  }

  private syncTemplateInputs() {
    this.template.user = this.user;
    this.template.offerSet = this.offerSet;
    this.template.offer = this.offer;
    this.template.previewing = this.previewing;
    this.template.step = this.step;
  }

  get period(): string {
    let offerId = this.rawOfferUUID;
    if (offerId && offerId.startsWith('p:')) {
      let [type, price, period] = offerId.split(/:/);
      return period;
    } else {
      return this.offer ? this.offer.period : null;
    }
  }

  get enablePixelDebugger() {
    return this.shell.hasFeatureSync('apps.accounts.enable_pixel_debugger');
  }
}
